import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import LogoReel from "@/components/LogoReel";

export default function Layout({ children }) {
  const { t, locale, setLocale } = useI18n();
  const location = useLocation();
  const isProfile = location.pathname.startsWith("/brands/") && location.pathname.split("/").length === 3;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bp-bg)" }}>
      <header className="border-b" style={{ borderColor: "var(--bp-border)" }}>
        <div className="bp-container py-5 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-baseline gap-3" data-testid="header-logo">
            <span className="font-serif text-2xl md:text-3xl leading-none tracking-tight" style={{ color: "var(--bp-text)" }}>
              BrandPassport
            </span>
            <span className="hidden md:inline text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
              {t.tagline}
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm" style={{ color: "var(--bp-text-secondary)" }}>
            <Link to="/brands" className="hover:underline" data-testid="nav-brands">{t.nav_brands}</Link>
            <Link to="/dpp-explainer" className="hover:underline" data-testid="nav-dpp">{t.nav_dpp}</Link>
            <Link to="/methodology" className="hover:underline" data-testid="nav-methodology">{t.nav_methodology}</Link>
            <Link to="/for-brands" className="hover:underline" data-testid="nav-for-brands">{t.nav_for_brands}</Link>
            <Link to="/about" className="hover:underline" data-testid="nav-about">{t.nav_about}</Link>
          </nav>
          <div className="flex items-center gap-1 text-xs font-mono" data-testid="lang-switch">
            <button
              onClick={() => setLocale("en")}
              data-testid="lang-en"
              className={`px-2 py-1 border ${locale === "en" ? "bg-black text-white border-black" : ""}`}
              style={{ borderColor: "var(--bp-border)" }}
            >
              EN
            </button>
            <button
              onClick={() => setLocale("it")}
              data-testid="lang-it"
              className={`px-2 py-1 border ${locale === "it" ? "bg-black text-white border-black" : ""}`}
              style={{ borderColor: "var(--bp-border)" }}
            >
              IT
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      {isProfile && (
        <div className="border-t border-b py-4" style={{ background: "var(--bp-bg-secondary)", borderColor: "var(--bp-border)" }} data-testid="persistent-disclaimer">
          <div className="bp-container text-xs md:text-sm leading-relaxed" style={{ color: "var(--bp-text-secondary)" }}>
            {t.footer_disclaimer}
          </div>
        </div>
      )}

      <footer className="border-t mt-16" style={{ borderColor: "var(--bp-border)" }}>
        <div className="bp-container py-10">
          <div className="mb-8">
            <LogoReel compact />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
            <div>
              <div className="font-serif text-xl mb-3">BrandPassport</div>
              <div className="text-xs" style={{ color: "var(--bp-text-muted)" }}>{t.tagline}</div>
            </div>
            <div>
              <div className="uppercase text-xs tracking-widest mb-3" style={{ color: "var(--bp-text-muted)" }}>Index</div>
              <ul className="space-y-2">
                <li><Link to="/brands" className="bp-link">{t.nav_brands}</Link></li>
                <li><Link to="/sectors/fashion" className="bp-link">Fashion</Link></li>
                <li><Link to="/sectors/furniture" className="bp-link">Furniture</Link></li>
              </ul>
            </div>
            <div>
              <div className="uppercase text-xs tracking-widest mb-3" style={{ color: "var(--bp-text-muted)" }}>Learn</div>
              <ul className="space-y-2">
                <li><Link to="/dpp-explainer" className="bp-link">{t.nav_dpp}</Link></li>
                <li><Link to="/methodology" className="bp-link">{t.nav_methodology}</Link></li>
                <li><Link to="/about" className="bp-link">{t.nav_about}</Link></li>
                <li><Link to="/for-brands" className="bp-link">{t.nav_for_brands}</Link></li>
              </ul>
            </div>
            <div>
              <div className="uppercase text-xs tracking-widest mb-3" style={{ color: "var(--bp-text-muted)" }}>Data</div>
              <ul className="space-y-2">
                <li><Link to="/data" className="bp-link">Data &amp; methodology</Link></li>
                <li><a href={`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`} className="bp-link">Sitemap</a></li>
                <li><a href={`${process.env.REACT_APP_BACKEND_URL}/api/feed.rss`} className="bp-link">RSS feed</a></li>
                <li><a href={`${process.env.REACT_APP_BACKEND_URL}/api/data/aggregates.csv`} className="bp-link">CSV export</a></li>
                <li><Link to="/crawl-policy" className="bp-link">Crawl policy</Link></li>
                <li><a href={`${process.env.REACT_APP_BACKEND_URL}/api/robots.txt`} className="bp-link">robots.txt</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t text-xs" style={{ borderColor: "var(--bp-border)", color: "var(--bp-text-muted)" }}>
            © {new Date().getFullYear()} BrandPassport. {t.footer_disclaimer}
          </div>
        </div>
      </footer>
    </div>
  );
}
